/*
  The family tree as the screens use it: library, family, sub-family, main parent, item (DEC-1016-51).

  The server sends nodes with a parent; this file turns them into a model that is easy to walk: children,
  roots, the progress of every node (its own, or the sum of what is below it), what is open and what is
  folded, and the breadcrumb of any node. Everything here is a pure function, so it is tested without a page.
*/

import type { Crumb, ElementKind, FamilyTree, Id, Origin, TreeProgress } from "../../../api/types";
import { isGone, toDisplayState, type DisplayState } from "./state";

export interface NavNode {
  id: Id;
  label: string;
  /** The level as the server named it ("library", "family", "sub_family", "main_parent", "item"). */
  level: string;
  kind: ElementKind;
  parentId: Id | null;
  state: DisplayState | null;
  origin: Origin;
  /** A parent item that is never drawn (it has no symbol and no map of its own). */
  abstract: boolean;
  drawn: boolean;
  /** The node's own progress, when the server gave one. */
  progress: TreeProgress | null;
}

export type EdgeKind = "main_parent" | "heading";

export interface NavModel {
  nodes: NavNode[];
  byId: Map<Id, NavNode>;
  childrenOf: Map<Id, NavNode[]>;
  roots: NavNode[];
  /** For each node with a parent: is it joined by a main-parent edge or a heading edge. */
  edgeKind: Map<Id, EdgeKind>;
  /** Own progress, or the sum of the nearest nodes below that have one. Null when there is nothing to count. */
  progress: Map<Id, TreeProgress | null>;
  /** How many nodes sit below each node. */
  below: Map<Id, number>;
  /** How deep each node sits: roots are 0. */
  depth: Map<Id, number>;
}

const CRUMB_LEVELS: readonly Crumb["level"][] = ["library", "family", "sub_family", "main_parent", "item"];

function sum(a: TreeProgress | null, b: TreeProgress): TreeProgress {
  return a ? { validated: a.validated + b.validated, waiting: a.waiting + b.waiting, total: a.total + b.total } : { ...b };
}

/** Build the model from nodes already in the screen's own form. */
export function buildNavModelFromNodes(input: readonly NavNode[], edgeKinds: ReadonlyMap<Id, EdgeKind> = new Map()): NavModel {
  const nodes = input.filter((node) => !!node.id);
  const byId = new Map<Id, NavNode>();
  for (const node of nodes) if (!byId.has(node.id)) byId.set(node.id, node);

  const childrenOf = new Map<Id, NavNode[]>();
  const roots: NavNode[] = [];
  for (const node of byId.values()) {
    const parent = node.parentId && node.parentId !== node.id ? byId.get(node.parentId) : undefined;
    if (!parent) {
      roots.push(node);
      continue;
    }
    const list = childrenOf.get(parent.id);
    if (list) list.push(node);
    else childrenOf.set(parent.id, [node]);
  }

  // A parent chain that loops (bad data) would never reach a root: make the loop's first node a root.
  const reachable = new Set<Id>();
  const walk = (node: NavNode) => {
    if (reachable.has(node.id)) return;
    reachable.add(node.id);
    for (const child of childrenOf.get(node.id) ?? []) walk(child);
  };
  roots.forEach(walk);
  for (const node of byId.values()) {
    if (reachable.has(node.id)) continue;
    const parentList = node.parentId ? childrenOf.get(node.parentId) : undefined;
    if (parentList) childrenOf.set(node.parentId as Id, parentList.filter((child) => child.id !== node.id));
    roots.push(node);
    walk(node);
  }

  const depth = new Map<Id, number>();
  const below = new Map<Id, number>();
  const progress = new Map<Id, TreeProgress | null>();
  const visit = (node: NavNode, level: number): { count: number; sum: TreeProgress | null } => {
    depth.set(node.id, level);
    let count = 0;
    let nearest: TreeProgress | null = null;
    for (const child of childrenOf.get(node.id) ?? []) {
      const result = visit(child, level + 1);
      count += 1 + result.count;
      const childValue = progress.get(child.id) ?? null;
      if (childValue) nearest = sum(nearest, childValue);
    }
    below.set(node.id, count);
    progress.set(node.id, node.progress ?? nearest);
    return { count, sum: nearest };
  };
  roots.forEach((root) => visit(root, 0));

  const kinds = new Map<Id, EdgeKind>(edgeKinds);
  for (const node of byId.values()) {
    if (!node.parentId || kinds.has(node.id)) continue;
    const parent = byId.get(node.parentId);
    if (parent) kinds.set(node.id, parent.kind === "item" ? "main_parent" : "heading");
  }

  return { nodes: Array.from(byId.values()), byId, childrenOf, roots, edgeKind: kinds, progress, below, depth };
}

/** Build the model from the family tree of the server. A node whose state says it is gone is left out. */
export function buildNavModel(tree: FamilyTree | undefined | null): NavModel {
  if (!tree) return buildNavModelFromNodes([]);
  const nodes: NavNode[] = tree.nodes
    .filter((node) => !isGone(node.state))
    .map((node) => ({
      id: node.id,
      label: node.label,
      level: node.level,
      kind: node.kind,
      parentId: node.parent_id ?? null,
      state: toDisplayState(node.state, node.origin),
      origin: node.origin,
      abstract: node.abstract,
      drawn: node.drawn,
      progress: node.progress ?? null,
    }));
  // The edges name the kind of join. Which end is the parent is told by `parent_id`, so either way round is fine.
  const kinds = new Map<Id, EdgeKind>();
  const parents = new Map(nodes.map((node) => [node.id, node.parentId] as const));
  for (const edge of tree.edges) {
    if (parents.get(edge.to) === edge.from) kinds.set(edge.to, edge.kind);
    else if (parents.get(edge.from) === edge.to) kinds.set(edge.from, edge.kind);
  }
  return buildNavModelFromNodes(nodes, kinds);
}

/** The nodes above a node, from the top, not including the node. */
export function ancestorsOf(model: NavModel, id: Id): NavNode[] {
  const out: NavNode[] = [];
  const seen = new Set<Id>([id]);
  let current = model.byId.get(id);
  while (current?.parentId) {
    const parent = model.byId.get(current.parentId);
    if (!parent || seen.has(parent.id)) break;
    seen.add(parent.id);
    out.unshift(parent);
    current = parent;
  }
  return out;
}

/** The level a breadcrumb names for a node. */
export function crumbLevel(model: NavModel, node: NavNode): Crumb["level"] {
  const named = CRUMB_LEVELS.find((level) => level === node.level);
  if (named) return named;
  switch (node.kind) {
    case "library":
      return "library";
    case "family":
      return "family";
    case "sub_family":
      return "sub_family";
    default:
      return (model.childrenOf.get(node.id) ?? []).some((child) => child.kind === "item") ? "main_parent" : "item";
  }
}

/** The family breadcrumb of a node, ending with the node itself. */
export function crumbsOf(model: NavModel, id: Id): Crumb[] {
  const node = model.byId.get(id);
  if (!node) return [];
  return [...ancestorsOf(model, id), node].map((n) => ({ element_id: n.id, label: n.label, level: crumbLevel(model, n) }));
}

/** The headings a node sits under, as one line: "Library › Family › Sub-family". Items above it are not part of it. */
export function headingPath(model: NavModel, id: Id): string {
  return ancestorsOf(model, id)
    .filter((node) => node.kind !== "item")
    .map((node) => node.label)
    .join(" › ");
}

/** The library a node belongs to (the top of its chain), or the node itself when it is one. */
export function libraryOf(model: NavModel, id: Id): NavNode | undefined {
  const chain = [...ancestorsOf(model, id)];
  const node = model.byId.get(id);
  if (node) chain.push(node);
  return chain[0];
}

/** A calm hue for each library, in the order the libraries come: 1 to 6, then again. */
export function hueIndexes(model: NavModel): Map<Id, number> {
  const out = new Map<Id, number>();
  model.roots.forEach((root, index) => out.set(root.id, (index % 6) + 1));
  return out;
}

export function hueOf(model: NavModel, hues: Map<Id, number>, id: Id): number {
  const root = libraryOf(model, id);
  return (root && hues.get(root.id)) || 1;
}

/** Every node that has something below it. */
export function foldable(model: NavModel): NavNode[] {
  return model.nodes.filter((node) => (model.childrenOf.get(node.id)?.length ?? 0) > 0);
}

/** How many nodes a tree may have before the first view folds it to the families. */
export const OPEN_ALL_LIMIT = 80;

/**
 * What is open when the person first looks. A small tree is open all the way; a large one shows the libraries
 * and their families, and the rest is one click away.
 */
export function defaultExpanded(model: NavModel): Set<Id> {
  const parents = foldable(model);
  if (model.nodes.length <= OPEN_ALL_LIMIT) return new Set(parents.map((node) => node.id));
  return new Set(parents.filter((node) => (model.depth.get(node.id) ?? 0) < 2).map((node) => node.id));
}

export interface VisibleRow {
  node: NavNode;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
}

/** The rows to draw, in tree order, with what is folded left out. */
export function visibleRows(model: NavModel, expanded: ReadonlySet<Id>): VisibleRow[] {
  const rows: VisibleRow[] = [];
  const walk = (node: NavNode, depth: number) => {
    const children = model.childrenOf.get(node.id) ?? [];
    const open = children.length > 0 && expanded.has(node.id);
    rows.push({ node, depth, hasChildren: children.length > 0, expanded: open });
    if (open) for (const child of children) walk(child, depth + 1);
  };
  model.roots.forEach((root) => walk(root, 0));
  return rows;
}

/** The set with the ancestors of a node added, so the node can be seen. */
export function withAncestorsOpen(model: NavModel, expanded: ReadonlySet<Id>, id: Id): Set<Id> {
  const out = new Set(expanded);
  for (const ancestor of ancestorsOf(model, id)) out.add(ancestor.id);
  return out;
}

/** Words for the level of a node, in the glossary's terms. */
export function levelWords(model: NavModel, node: NavNode): string {
  switch (crumbLevel(model, node)) {
    case "library":
      return "Library";
    case "family":
      return "Family";
    case "sub_family":
      return "Sub-family";
    case "main_parent":
      return "Parent item";
    case "item":
      return "Item";
  }
}

/** Words for the progress of a node: "3 of 8 validated, 2 waiting for the SME". */
export function progressWords(value: TreeProgress | null | undefined): string {
  if (!value || value.total <= 0) return "Nothing to count yet";
  return `${value.validated} of ${value.total} validated, ${value.waiting} waiting for the SME`;
}
